import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Edit2, Save, Home, Users, Download, Trophy, Check, GripVertical, LockOpen, Archive, RotateCcw, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EVENT_LOGO = "https://customer-assets.emergentagent.com/job_fight-forecast-1/artifacts/rof1u6za_LOGO_HAMARA_TUMMA.png";

function SortableFightRow({ fight, onEdit, onDelete, onSetResult, onToggleLock }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: fight.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [method, setMethod] = useState(fight.result_method || "");
  const [showMethod, setShowMethod] = useState(false);

  return (
    <div ref={setNodeRef} style={style}
      className={cn("bg-[#121212] border p-4",
        fight.result ? 'border-white/20' : fight.is_locked ? 'border-yellow-500/30' : 'border-white/10'
      )}
      data-testid={`admin-fight-${fight.id}`}>
      
      {/* Locked banner */}
      {fight.is_locked && !fight.result && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 px-3 py-1.5 mb-3 flex items-center justify-between">
          <span className="text-xs font-bold text-yellow-500 uppercase tracking-wider">Ottelu käynnissä — veikkaukset lukittu</span>
          <Button onClick={() => onToggleLock(fight.id)} size="sm"
            className="rounded-none text-[10px] uppercase bg-transparent border border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 h-6 px-2"
            data-testid={`unlock-fight-${fight.id}`}>
            <LockOpen className="w-3 h-3 mr-1" /> Avaa
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 touch-none"
            data-testid={`drag-handle-${fight.id}`}>
            <GripVertical className="w-5 h-5" />
          </button>
          <div className="text-sm text-[#A1A1AA] w-8">#{fight.fight_order}</div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={fight.result === 'fighter1' ? 'text-white font-bold' : 'text-white'}>
                {fight.fighter1.name}
                {fight.result === 'fighter1' && <Check className="inline w-4 h-4 ml-1" />}
              </span>
              <span className="text-white/40">vs</span>
              <span className={fight.result === 'fighter2' ? 'text-white font-bold' : 'text-white'}>
                {fight.fighter2.name}
                {fight.result === 'fighter2' && <Check className="inline w-4 h-4 ml-1" />}
              </span>
              {fight.is_main_event && (
                <span className="bg-[#FF3B30] text-white text-[10px] px-2 py-0.5 uppercase tracking-wider">Main Event</span>
              )}
            </div>
            <div className="text-xs text-[#A1A1AA]">
              {fight.fight_type && <span className="text-[#FF3B30]">{fight.fight_type} &middot; </span>}
              {fight.weight_class}
              {fight.result_method && <span className="text-white/60 ml-2">({fight.result_method})</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!fight.is_locked && !fight.result && (
            <Button onClick={() => onToggleLock(fight.id)} variant="outline" size="sm"
              className="bg-transparent border border-white/20 text-white/40 hover:bg-yellow-500/10 hover:text-yellow-500 hover:border-yellow-500/50 rounded-none"
              data-testid={`lock-fight-${fight.id}`}>
              <Lock className="w-4 h-4" />
            </Button>
          )}
          <Button onClick={() => onEdit(fight)} variant="outline" size="sm"
            className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-none"
            data-testid={`edit-fight-${fight.id}`}>
            <Edit2 className="w-4 h-4" />
          </Button>
          <Button onClick={() => onDelete(fight.id)} variant="outline" size="sm"
            className="bg-transparent border border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none"
            data-testid={`delete-fight-${fight.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {/* Result Selector */}
      <div className="flex items-center gap-3 pt-3 border-t border-white/10 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-[#A1A1AA]">Result:</span>
        <Button onClick={() => { setShowMethod(true); onSetResult(fight.id, 'fighter1', method); }} size="sm"
          className={`rounded-none text-xs uppercase ${fight.result === 'fighter1' ? 'bg-[#FF3B30] text-white' : 'bg-transparent border border-[#FF3B30]/50 text-[#FF3B30] hover:bg-[#FF3B30]/10'}`}
          data-testid={`set-result-fighter1-${fight.id}`}>
          {fight.fighter1.name} wins
        </Button>
        <Button onClick={() => { setShowMethod(true); onSetResult(fight.id, 'fighter2', method); }} size="sm"
          className={`rounded-none text-xs uppercase ${fight.result === 'fighter2' ? 'bg-[#007AFF] text-white' : 'bg-transparent border border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10'}`}
          data-testid={`set-result-fighter2-${fight.id}`}>
          {fight.fighter2.name} wins
        </Button>
        <Button onClick={() => onSetResult(fight.id, 'draw', method)} size="sm"
          className={`rounded-none text-xs uppercase ${fight.result === 'draw' ? 'bg-white/20 text-white' : 'bg-transparent border border-white/30 text-white/50 hover:bg-white/10'}`}
          data-testid={`set-result-draw-${fight.id}`}>
          Tasapeli
        </Button>
        {fight.result && (
          <Button onClick={() => onSetResult(fight.id, 'none', null)} size="sm"
            className="rounded-none text-xs uppercase bg-transparent border border-white/20 text-white/60 hover:bg-white/5"
            data-testid={`clear-result-${fight.id}`}>
            Clear
          </Button>
        )}
        {(fight.result || showMethod) && (
          <Input value={method} onChange={(e) => setMethod(e.target.value)}
            onBlur={() => { if (fight.result && method !== (fight.result_method || '')) onSetResult(fight.id, fight.result, method); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && fight.result) onSetResult(fight.id, fight.result, method); }}
            placeholder="How? (KO R1, Sub R2, Dec...)"
            className="bg-transparent border border-white/20 text-white text-xs rounded-none w-48 h-8 focus:border-[#FF3B30]"
            data-testid={`result-method-${fight.id}`} />
        )}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [fights, setFights] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bonusVotes, setBonusVotes] = useState({});
  const [fastestFinishResult, setFastestFinishResult] = useState(null);
  const [fastestFinishAnalytics, setFastestFinishAnalytics] = useState(null);
  const [ffForm, setFfForm] = useState({ fighter_name: "", round: "", minute: "", second: "" });
  const [siteTexts, setSiteTexts] = useState({});
  const [voteTally, setVoteTally] = useState({});
  const [voteEmails, setVoteEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingFight, setEditingFight] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("fights");
  const [archivedEvents, setArchivedEvents] = useState([]);
  const [archiveEventName, setArchiveEventName] = useState("");
  const [archiving, setArchiving] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const makeEmptyForm = () => ({
    fighter1: { name: "", record: "", hometown: "", image_url: "" },
    fighter2: { name: "", record: "", hometown: "", image_url: "" },
    weight_class: "", fight_type: "", fight_order: 0, is_main_event: false
  });
  const [formData, setFormData] = useState(makeEmptyForm);

  const resetForm = () => { setFormData(makeEmptyForm()); setEditingFight(null); };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { 'x-admin-password': password } });
      setAuthenticated(true);
      sessionStorage.setItem('adminPassword', password);
      fetchData();
    } catch { toast.error("Invalid password"); }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem('adminPassword');
    if (saved) {
      setPassword(saved);
      axios.post(`${API}/admin/verify`, {}, { headers: { 'x-admin-password': saved } })
        .then(() => { setAuthenticated(true); fetchData(); })
        .catch(() => sessionStorage.removeItem('adminPassword'));
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const pwd = sessionStorage.getItem('adminPassword');
    try {
      const [f, p, l, bv, ffr, ffa, sc, vq, vt, ve, ae] = await Promise.all([
        axios.get(`${API}/fights`),
        axios.get(`${API}/predictions`, { headers: { 'x-admin-password': pwd } }),
        axios.get(`${API}/predictions/leaderboard`),
        axios.get(`${API}/bonus-votes`, { headers: { 'x-admin-password': pwd } }),
        axios.get(`${API}/fastest-finish-result`, { headers: { 'x-admin-password': pwd } }),
        axios.get(`${API}/fastest-finish-analytics`, { headers: { 'x-admin-password': pwd } }).catch(() => ({ data: null })),
        axios.get(`${API}/site-content`),
        axios.get(`${API}/vote-questions`),
        axios.get(`${API}/votes/tally`, { headers: { 'x-admin-password': pwd } }).catch(() => ({ data: {} })),
        axios.get(`${API}/vote-emails`, { headers: { 'x-admin-password': pwd } }).catch(() => ({ data: [] })),
        axios.get(`${API}/events`).catch(() => ({ data: [] }))
      ]);
      setFights(f.data); setPredictions(p.data); setLeaderboard(l.data); setBonusVotes(bv.data);
      setVoteTally(vt.data || {});
      setVoteEmails(ve.data || []);
      setArchivedEvents(ae.data || []);
      // Merge site content + vote questions into siteTexts
      const mergedTexts = { ...(sc.data.texts || {}) };
      if (vq.data.questions) {
        Object.entries(vq.data.questions).forEach(([k, v]) => { mergedTexts[`vote_${k}`] = v; });
      }
      setSiteTexts(mergedTexts);
      setFastestFinishResult(ffr.data.result);
      if (ffa.data) setFastestFinishAnalytics(ffa.data);
      if (ffr.data.result) {
        setFfForm({
          fighter_name: ffr.data.result.fighter_name,
          round: String(ffr.data.result.round),
          minute: String(ffr.data.result.minute),
          second: String(ffr.data.result.second)
        });
      }
    } catch { toast.error("Failed to fetch data"); }
    finally { setLoading(false); }
  }, []);

  const handleSaveFight = async (e) => {
    e.preventDefault();
    const pwd = sessionStorage.getItem('adminPassword');
    try {
      if (editingFight) {
        await axios.put(`${API}/fights/${editingFight.id}`, formData, { headers: { 'x-admin-password': pwd } });
        toast.success("Fight updated");
      } else {
        await axios.post(`${API}/fights`, formData, { headers: { 'x-admin-password': pwd } });
        toast.success("Fight created");
      }
      fetchData(); setIsDialogOpen(false); resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save fight"); }
  };

  const handleDeleteFight = async (fightId) => {
    if (!window.confirm("Delete this fight? Predictions for it will be ignored in scoring.")) return;
    try {
      await axios.delete(`${API}/fights/${fightId}`, { headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') } });
      toast.success("Fight deleted"); fetchData();
    } catch { toast.error("Failed to delete fight"); }
  };

  const handleEditFight = (fight) => {
    setEditingFight(fight);
    setFormData({
      fighter1: { name: fight.fighter1?.name || "", record: fight.fighter1?.record || "", hometown: fight.fighter1?.hometown || "", image_url: fight.fighter1?.image_url || "" },
      fighter2: { name: fight.fighter2?.name || "", record: fight.fighter2?.record || "", hometown: fight.fighter2?.hometown || "", image_url: fight.fighter2?.image_url || "" },
      weight_class: fight.weight_class, fight_type: fight.fight_type || "",
      fight_order: fight.fight_order, is_main_event: fight.is_main_event
    });
    setIsDialogOpen(true);
  };

  const handleSetResult = async (fightId, result, method) => {
    try {
      await axios.put(`${API}/fights/${fightId}/result`,
        { result, result_method: method || null },
        { headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') } });
      toast.success(result === 'none' ? 'Result cleared' : 'Result set'); fetchData();
    } catch { toast.error("Failed to set result"); }
  };

  const handleToggleLock = async (fightId) => {
    try {
      const res = await axios.put(`${API}/fights/${fightId}/lock`, {}, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success(res.data.is_locked ? 'Fight locked — predictions blocked' : 'Fight unlocked');
      fetchData();
    } catch { toast.error("Failed to toggle lock"); }
  };


  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fights.findIndex(f => f.id === active.id);
    const newIndex = fights.findIndex(f => f.id === over.id);
    const reordered = arrayMove(fights, oldIndex, newIndex);
    setFights(reordered);
    const reorderData = reordered.map((f, i) => ({ id: f.id, fight_order: i + 1 }));
    try {
      await axios.put(`${API}/fights/reorder`, { fights: reorderData },
        { headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') } });
      toast.success("Fight order updated"); fetchData();
    } catch { toast.error("Failed to reorder"); }
  };

  const allFighters = fights.flatMap(f => [f.fighter1?.name, f.fighter2?.name].filter(Boolean));

  const handleSaveContent = async () => {
    try {
      await axios.put(`${API}/site-content`, { texts: siteTexts }, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success("Content saved!"); fetchData();
    } catch { toast.error("Failed to save content"); }
  };


  const handleSaveFastestFinish = async () => {
    if (!ffForm.fighter_name || !ffForm.round) { toast.error("Select fighter and round"); return; }
    try {
      await axios.put(`${API}/fastest-finish-result`, {
        fighter_name: ffForm.fighter_name,
        round: parseInt(ffForm.round),
        minute: parseInt(ffForm.minute || "0"),
        second: parseInt(ffForm.second || "0")
      }, { headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') } });
      toast.success("Fastest finish result saved"); fetchData();
    } catch { toast.error("Failed to save"); }
  };


  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/predictions/export`, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hamara_mma_predictions_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel downloaded!");
    } catch { toast.error("Failed to export"); }
  };

  const handleDeletePrediction = async (predId) => {
    try {
      await axios.delete(`${API}/predictions/${predId}`, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success("Prediction deleted"); fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteAllPredictions = async () => {
    if (!window.confirm("Delete ALL predictions? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/predictions`, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success("All predictions deleted"); fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteAllVotes = async () => {
    if (!window.confirm("Delete ALL audience votes and vote emails? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/votes`, { headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') } });
      toast.success("All votes deleted"); fetchData();
    } catch { toast.error("Failed to delete votes"); }
  };

  const [uploading, setUploading] = useState({ fighter1: false, fighter2: false });
  const handleImageUpload = async (fighterKey, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Kuva on liian suuri (max 5MB)"); return; }
    setUploading(prev => ({ ...prev, [fighterKey]: true }));
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/upload/fighter-image`, form, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setFormData(prev => ({ ...prev, [fighterKey]: { ...prev[fighterKey], image_url: imageUrl } }));
      toast.success("Kuva ladattu!");
    } catch (err) {
      const detail = err.response?.data?.detail;
      console.error("Image upload failed:", err.response?.status, detail);
      toast.error(detail || "Kuvan lataus epäonnistui");
    }
    finally { setUploading(prev => ({ ...prev, [fighterKey]: false })); }
  };

  const handleExportVoteEmails = async () => {
    try {
      const response = await axios.get(`${API}/vote-emails/export`, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hamara_vote_emails_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Vote emails exported!");
    } catch { toast.error("Failed to export"); }
  };

  const handleArchiveEvent = async () => {
    if (!archiveEventName.trim()) { toast.error("Syötä tapahtuman nimi"); return; }
    if (!window.confirm(`Arkistoidaan "${archiveEventName}"? Kaikki nykyiset ottelut, veikkaukset ja äänet siirretään arkistoon ja poistetaan aktiivisesta näkymästä.`)) return;
    setArchiving(true);
    try {
      await axios.post(`${API}/events/archive`, { event_name: archiveEventName }, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success(`"${archiveEventName}" arkistoitu!`);
      setArchiveEventName("");
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Arkistointi epäonnistui"); }
    finally { setArchiving(false); }
  };

  const handleUnarchiveEvent = async (eventId, eventName) => {
    if (!window.confirm(`Palautetaan "${eventName}"? Nykyiset aktiiviset ottelut ja veikkaukset korvataan arkistoidulla datalla.`)) return;
    try {
      await axios.post(`${API}/events/${eventId}/unarchive`, {}, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success(`"${eventName}" palautettu!`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Palautus epäonnistui"); }
  };

  const handleDeleteArchivedEvent = async (eventId, eventName) => {
    if (!window.confirm(`Poistetaan arkistoitu tapahtuma "${eventName}" pysyvästi? Tätä ei voi perua.`)) return;
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { 'x-admin-password': sessionStorage.getItem('adminPassword') }
      });
      toast.success("Arkistoitu tapahtuma poistettu");
      fetchData();
    } catch { toast.error("Poisto epäonnistui"); }
  };

  const decidedCount = fights.filter(f => f.result).length;
  const totalVoteCount = useMemo(() => 
    Object.values(voteTally).reduce((sum, cat) => sum + cat.reduce((s, v) => s + v.votes, 0), 0),
    [voteTally]
  );

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#121212] border border-white/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-[#FF3B30]" />
            </div>
            <h1 className="text-3xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Admin Access</h1>
          </div>
          <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
            <Input type="password" placeholder="Enter admin password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none py-3"
              data-testid="admin-password-input" />
            <Button type="submit" className="w-full bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider py-3 rounded-none"
              data-testid="admin-login-button">Enter</Button>
          </form>
          <div className="mt-8 text-center">
            <a href="/" className="text-sm text-white/40 hover:text-white/60 flex items-center justify-center gap-2">
              <Home className="w-4 h-4" /> Back to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <header className="bg-[#121212] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={EVENT_LOGO} alt="Hamara MMA" className="h-8 object-contain" />
            <h1 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="admin-header">
              Admin Panel
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-sm text-white/60 hover:text-white flex items-center gap-2"><Home className="w-4 h-4" /> Site</a>
            <Button onClick={() => { sessionStorage.removeItem('adminPassword'); setAuthenticated(false); setPassword(""); }}
              variant="outline" className="bg-transparent border border-white/20 text-white hover:bg-white/5 rounded-none text-sm"
              data-testid="admin-logout-button">Logout</Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Fights</div>
            <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="total-fights-count">{fights.length}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Results</div>
            <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{decidedCount}/{fights.length}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Predictions</div>
            <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }} data-testid="total-predictions-count">{predictions.length}</div>
          </div>
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Lottery</div>
            <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{predictions.filter(p => p.marketing_consent).length}</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-white/10 overflow-x-auto">
          {["fights", "content", "leaderboard", "bonus", "votes", "predictions", "archive"].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`pb-4 px-2 text-sm uppercase tracking-wider font-bold transition-colors whitespace-nowrap ${activeTab === tab ? "text-[#FF3B30] border-b-2 border-[#FF3B30]" : "text-white/60 hover:text-white"}`}
              data-testid={`tab-${tab}`}>
              {tab === "fights" ? "Fights" : tab === "content" ? "Content" : tab === "leaderboard" ? "Leaderboard" : tab === "bonus" ? "Bonus" : tab === "votes" ? "Votes" : tab === "predictions" ? "Predictions" : "Archive"}
            </button>
          ))}
        </div>

        {/* Fights Tab */}
        {activeTab === "fights" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                Fight Card & Results
              </h2>
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-6 py-2 rounded-none"
                    data-testid="add-fight-button">
                    <Plus className="w-4 h-4 mr-2" /> Add Fight
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#121212] border border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto rounded-none">
                  <DialogHeader>
                    <DialogTitle className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                      {editingFight ? "Edit Fight" : "Add New Fight"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSaveFight} className="space-y-6 mt-4" data-testid="fight-form">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm uppercase tracking-wider text-[#A1A1AA]">Weight Class *</Label>
                        <Input value={formData.weight_class} onChange={(e) => setFormData({ ...formData, weight_class: e.target.value })}
                          placeholder="e.g., Lightweight" className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-2"
                          required data-testid="weight-class-input" />
                      </div>
                      <div>
                        <Label className="text-sm uppercase tracking-wider text-[#A1A1AA]">Fight Type</Label>
                        <Input value={formData.fight_type} onChange={(e) => setFormData({ ...formData, fight_type: e.target.value })}
                          placeholder="e.g., Pro MMA, Pro Muay Thai"
                          className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-2"
                          data-testid="fight-type-input" />
                      </div>
                    </div>

                    {/* Fighter 1 */}
                    <div className="border border-white/20 p-4">
                      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Fighter 1</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Name *</Label>
                          <Input value={formData.fighter1.name}
                            onChange={(e) => setFormData({ ...formData, fighter1: { ...formData.fighter1, name: e.target.value } })}
                            className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                            required data-testid="fighter1-name-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Hometown</Label>
                          <Input value={formData.fighter1.hometown}
                            onChange={(e) => setFormData({ ...formData, fighter1: { ...formData.fighter1, hometown: e.target.value } })}
                            placeholder="e.g., Helsinki, Finland"
                            className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                            data-testid="fighter1-hometown-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Record</Label>
                          <Input value={formData.fighter1.record}
                            onChange={(e) => setFormData({ ...formData, fighter1: { ...formData.fighter1, record: e.target.value } })}
                            placeholder="e.g., 15-3-0"
                            className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                            data-testid="fighter1-record-input" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Image</Label>
                          <div className="flex items-center gap-3 mt-1">
                            <label className={`flex items-center gap-2 px-4 py-2 border border-[#FF3B30]/50 text-[#FF3B30] text-xs uppercase tracking-wider cursor-pointer hover:bg-[#FF3B30]/10 transition-colors ${uploading.fighter1 ? 'opacity-50 pointer-events-none' : ''}`}
                              data-testid="fighter1-upload-btn">
                              <Upload className="w-3 h-3" /> {uploading.fighter1 ? "Ladataan..." : "Lataa kuva"}
                              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                                onChange={(e) => handleImageUpload('fighter1', e.target.files[0])} />
                            </label>
                            {formData.fighter1.image_url && (
                              <div className="w-10 h-10 overflow-hidden rounded-full border border-white/20 shrink-0">
                                <img src={formData.fighter1.image_url} alt="" className="w-full h-full object-cover object-top" />
                              </div>
                            )}
                            <Input value={formData.fighter1.image_url}
                              onChange={(e) => setFormData({ ...formData, fighter1: { ...formData.fighter1, image_url: e.target.value } })}
                              placeholder="tai liitä kuva-URL"
                              className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none flex-1 text-xs h-9"
                              data-testid="fighter1-image-input" />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Fighter 2 */}
                    <div className="border border-white/20 p-4">
                      <h3 className="text-white font-bold uppercase tracking-wider mb-4">Fighter 2</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Name *</Label>
                          <Input value={formData.fighter2.name}
                            onChange={(e) => setFormData({ ...formData, fighter2: { ...formData.fighter2, name: e.target.value } })}
                            className="bg-transparent border border-white/20 text-white focus:border-[#007AFF] rounded-none mt-1"
                            required data-testid="fighter2-name-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Hometown</Label>
                          <Input value={formData.fighter2.hometown}
                            onChange={(e) => setFormData({ ...formData, fighter2: { ...formData.fighter2, hometown: e.target.value } })}
                            placeholder="e.g., Turku, Finland"
                            className="bg-transparent border border-white/20 text-white focus:border-[#007AFF] rounded-none mt-1"
                            data-testid="fighter2-hometown-input" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Record</Label>
                          <Input value={formData.fighter2.record}
                            onChange={(e) => setFormData({ ...formData, fighter2: { ...formData.fighter2, record: e.target.value } })}
                            placeholder="e.g., 10-2-0"
                            className="bg-transparent border border-white/20 text-white focus:border-[#007AFF] rounded-none mt-1"
                            data-testid="fighter2-record-input" />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Image</Label>
                          <div className="flex items-center gap-3 mt-1">
                            <label className={`flex items-center gap-2 px-4 py-2 border border-[#007AFF]/50 text-[#007AFF] text-xs uppercase tracking-wider cursor-pointer hover:bg-[#007AFF]/10 transition-colors ${uploading.fighter2 ? 'opacity-50 pointer-events-none' : ''}`}
                              data-testid="fighter2-upload-btn">
                              <Upload className="w-3 h-3" /> {uploading.fighter2 ? "Ladataan..." : "Lataa kuva"}
                              <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden"
                                onChange={(e) => handleImageUpload('fighter2', e.target.files[0])} />
                            </label>
                            {formData.fighter2.image_url && (
                              <div className="w-10 h-10 overflow-hidden rounded-full border border-white/20 shrink-0">
                                <img src={formData.fighter2.image_url} alt="" className="w-full h-full object-cover object-top" />
                              </div>
                            )}
                            <Input value={formData.fighter2.image_url}
                              onChange={(e) => setFormData({ ...formData, fighter2: { ...formData.fighter2, image_url: e.target.value } })}
                              placeholder="tai liitä kuva-URL"
                              className="bg-transparent border border-white/20 text-white focus:border-[#007AFF] rounded-none flex-1 text-xs h-9"
                              data-testid="fighter2-image-input" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm uppercase tracking-wider text-[#A1A1AA]">Fight Order</Label>
                        <Input type="number" value={formData.fight_order}
                          onChange={(e) => setFormData({ ...formData, fight_order: parseInt(e.target.value) || 0 })}
                          className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-2"
                          data-testid="fight-order-input" />
                      </div>
                      <div className="flex items-center gap-4 pt-6">
                        <Switch checked={formData.is_main_event}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_main_event: checked })}
                          data-testid="main-event-switch" />
                        <Label className="text-sm uppercase tracking-wider">Main Event</Label>
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4">
                      <Button type="submit" className="flex-1 bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider py-3 rounded-none"
                        data-testid="save-fight-button">
                        <Save className="w-4 h-4 mr-2" /> {editingFight ? "Update" : "Create"}
                      </Button>
                      <Button type="button" onClick={() => { setIsDialogOpen(false); resetForm(); }}
                        className="bg-transparent border border-white/20 text-white hover:bg-white/5 font-bold uppercase tracking-wider px-6 py-3 rounded-none"
                        data-testid="cancel-fight-button">Cancel</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <p className="text-xs text-[#A1A1AA] mb-4">Drag the grip handles to reorder fights</p>

            {loading ? <div className="text-center py-12 text-[#A1A1AA]">Loading...</div>
            : fights.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10"><p className="text-[#A1A1AA]">No fights yet.</p></div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={fights.map(f => f.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-4" data-testid="fights-list">
                    {fights.map(fight => (
                      <SortableFightRow key={fight.id} fight={fight}
                        onEdit={handleEditFight} onDelete={handleDeleteFight} onSetResult={handleSetResult} onToggleLock={handleToggleLock} />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>
        )}


        {/* Content Tab */}
        {activeTab === "content" && (
          <section>
            <h2 className="text-2xl uppercase tracking-tight mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Edit Site Content</h2>
            
            <div className="space-y-6 max-w-3xl">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30]">Predictions Page</h3>
              {[
                { key: "hero_description", label: "Hero Description" },
                { key: "marketing_checkbox", label: "Marketing Checkbox Text" },
                { key: "confirmation_title", label: "Confirmation Title" },
                { key: "confirmation_text", label: "Confirmation Text" },
                { key: "submit_heading", label: "Submit Section Heading" },
                { key: "submit_button", label: "Submit Button Text" },
                { key: "fight_card_heading", label: "Fight Card Heading" },
                { key: "bonus_heading", label: "Bonus Section Heading" },
              ].map(field => (
                <div key={field.key}>
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">{field.label}</Label>
                  <Input value={siteTexts[field.key] || ""} 
                    onChange={(e) => setSiteTexts(prev => ({ ...prev, [field.key]: e.target.value }))}
                    className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                    data-testid={`content-${field.key}`} />
                </div>
              ))}

              <div className="pt-6 border-t border-white/10">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30] mb-4">Vote Page</h3>
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Vote Hero Description</Label>
                  <Input value={siteTexts.vote_hero_description || ""}
                    onChange={(e) => setSiteTexts(prev => ({ ...prev, vote_hero_description: e.target.value }))}
                    className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                    data-testid="content-vote-hero-description" />
                </div>
                <div className="mb-4">
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Vote Marketing Checkbox Text</Label>
                  <Input value={siteTexts.vote_marketing_checkbox || ""}
                    onChange={(e) => setSiteTexts(prev => ({ ...prev, vote_marketing_checkbox: e.target.value }))}
                    placeholder="Haluan osallistua lippupaketin arvontaan..."
                    className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                    data-testid="content-vote-marketing-checkbox" />
                </div>
                <h4 className="text-xs font-bold uppercase tracking-wider text-[#A1A1AA] mb-3">Vote Questions</h4>
                {[
                  { key: "best_fight", label: "Best Fight Question" },
                  { key: "best_fighter", label: "Best Fighter Question" },
                  { key: "surprise", label: "Surprise Question" },
                ].map(field => (
                  <div key={field.key} className="mb-4">
                    <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">{field.label}</Label>
                    <Input value={siteTexts[`vote_${field.key}`] || ""} 
                      onChange={(e) => setSiteTexts(prev => ({ ...prev, [`vote_${field.key}`]: e.target.value }))}
                      placeholder={field.key === "best_fight" ? "Paras ottelu" : field.key === "best_fighter" ? "Kovin ottelija" : "Ablemans: Illan yllättäjä"}
                      className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                      data-testid={`content-vote-${field.key}`} />
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <Button onClick={async () => {
                  try {
                    // Save site content
                    const siteFields = {};
                    const voteFields = {};
                    const voteQuestionKeys = ['vote_best_fight', 'vote_best_fighter', 'vote_surprise'];
                    Object.entries(siteTexts).forEach(([k, v]) => {
                      if (voteQuestionKeys.includes(k)) voteFields[k.replace('vote_', '')] = v;
                      else siteFields[k] = v;
                    });
                    const pwd = sessionStorage.getItem('adminPassword');
                    await Promise.all([
                      axios.put(`${API}/site-content`, { texts: siteFields }, { headers: { 'x-admin-password': pwd } }),
                      Object.keys(voteFields).length > 0 ? axios.put(`${API}/vote-questions`, { texts: voteFields }, { headers: { 'x-admin-password': pwd } }) : Promise.resolve()
                    ]);
                    toast.success("Content saved!"); fetchData();
                  } catch { toast.error("Failed to save"); }
                }}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-8 py-3 rounded-none"
                  data-testid="save-content-button">
                  <Save className="w-4 h-4 mr-2" /> Save All Content
                </Button>
              </div>
            </div>
          </section>
        )}


        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Leaderboard</h2>
              <div className="text-sm text-[#A1A1AA]">{decidedCount}/{fights.length} results</div>
            </div>
            {leaderboard.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <Trophy className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" /><p className="text-[#A1A1AA]">No predictions</p>
              </div>
            ) : (
              <div className="bg-[#121212] border border-white/10 overflow-hidden" data-testid="leaderboard-table">
                <table className="w-full">
                  <thead><tr className="border-b border-white/10">
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4 w-16">#</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Email</th>
                    <th className="text-center text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Correct</th>
                    <th className="text-center text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Lottery</th>
                  </tr></thead>
                  <tbody>
                    {leaderboard.map((entry, idx) => (
                      <tr key={entry.email} className={`border-b border-white/5 ${idx < 3 ? 'bg-[#FF3B30]/5' : ''}`}>
                        <td className="p-4">{idx < 3 ? ['🥇','🥈','🥉'][idx] : <span className="text-white/40">{idx+1}</span>}</td>
                        <td className="p-4">{entry.email}</td>
                        <td className="p-4 text-center">
                          <span className="text-2xl font-bold text-[#FF3B30]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{entry.correct}</span>
                          <span className="text-white/40 text-sm">/{entry.total_scored}</span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`text-xs px-2 py-1 ${entry.marketing_consent ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}>
                            {entry.marketing_consent ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Bonus Votes Tab */}
        {activeTab === "bonus" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Bonus</h2>
            </div>
            
            {/* Fastest Finish - Set actual result */}
            <div className="bg-[#121212] border border-white/10 p-6 mb-8">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30] mb-4">Set Actual Fastest Finish Result</h3>
              <div className="flex flex-wrap gap-3 items-end">
                <div className="flex-1 min-w-[160px]">
                  <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Fighter</label>
                  <Select value={ffForm.fighter_name} onValueChange={(val) => setFfForm({ ...ffForm, fighter_name: val })}>
                    <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0" data-testid="ff-fighter-select">
                      <SelectValue placeholder="Select fighter" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none max-h-48 overflow-y-auto">
                      {allFighters.map(f => (
                        <SelectItem key={f} value={f} className="focus:bg-white/10 focus:text-white rounded-none">{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-20">
                  <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Round</label>
                  <Select value={ffForm.round} onValueChange={(val) => setFfForm({ ...ffForm, round: val })}>
                    <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0"><SelectValue placeholder="Rd" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                      {[1,2,3,4,5].map(r => <SelectItem key={r} value={String(r)} className="focus:bg-white/10 focus:text-white rounded-none">R{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-20">
                  <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Min</label>
                  <Select value={ffForm.minute} onValueChange={(val) => setFfForm({ ...ffForm, minute: val })}>
                    <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0"><SelectValue placeholder="Min" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                      {[0,1,2,3,4,5].map(m => <SelectItem key={m} value={String(m)} className="focus:bg-white/10 focus:text-white rounded-none">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-20">
                  <label className="text-[9px] uppercase tracking-wider text-[#A1A1AA] block mb-1">Sec</label>
                  <Select value={ffForm.second} onValueChange={(val) => setFfForm({ ...ffForm, second: val })}>
                    <SelectTrigger className="bg-transparent border border-white/20 text-white rounded-none focus:ring-0"><SelectValue placeholder="Sec" /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border border-white/20 text-white rounded-none">
                      {Array.from({length: 60}, (_, i) => i).map(s => <SelectItem key={s} value={String(s)} className="focus:bg-white/10 focus:text-white rounded-none">{String(s).padStart(2,'0')}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleSaveFastestFinish}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-6 py-2 rounded-none"
                  data-testid="save-fastest-finish-btn">
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
              {fastestFinishResult && (
                <p className="text-xs text-[#A1A1AA] mt-3">
                  Current: <span className="text-white font-bold">{fastestFinishResult.fighter_name}</span> — Erä {fastestFinishResult.round}, {fastestFinishResult.minute}:{String(fastestFinishResult.second).padStart(2,'0')}
                </p>
              )}
            </div>

            {/* Fastest Finish Analytics */}
            {fastestFinishAnalytics && fastestFinishAnalytics.actual && (
              <div className="bg-[#121212] border border-white/10 overflow-hidden mb-8">
                <div className="bg-[#FF3B30]/10 border-b border-white/10 px-4 py-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30]">
                    Nopein Lopetus — Predictions vs Actual
                  </h3>
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    Actual: <span className="text-white">{fastestFinishAnalytics.actual.fighter_name}</span> — {fastestFinishAnalytics.actual.time}
                  </p>
                </div>
                <table className="w-full" data-testid="fastest-finish-table">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">#</th>
                      <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Email</th>
                      <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Fighter Pick</th>
                      <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Time Pick</th>
                      <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Time Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fastestFinishAnalytics.comparisons.map((c, idx) => (
                      <tr key={c.email} className="border-b border-white/5">
                        <td className="p-4 text-white/40">{idx + 1}</td>
                        <td className="p-4">{c.email}</td>
                        <td className="p-4">
                          <span className={c.fighter_correct ? 'text-green-400 font-bold' : 'text-white/60'}>
                            {c.fighter_pick || '—'}
                          </span>
                          {c.fighter_correct && <span className="text-green-400 ml-1">✓</span>}
                        </td>
                        <td className="p-4 text-white/60">{c.time_pick || '—'}</td>
                        <td className="p-4">
                          {c.time_diff_seconds != null ? (
                            <span className={c.time_diff_seconds === 0 ? 'text-green-400 font-bold' : c.time_diff_seconds <= 30 ? 'text-yellow-400' : 'text-white/60'}>
                              {c.time_diff_seconds === 0 ? 'EXACT!' : `${Math.floor(c.time_diff_seconds / 60)}m ${c.time_diff_seconds % 60}s off`}
                            </span>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vote Tallies */}
            <h3 className="text-lg uppercase tracking-tight mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Vote Tallies</h3>
            {Object.keys(bonusVotes).length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <p className="text-[#A1A1AA]">No bonus votes yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(bonusVotes).map(([question, fighters]) => (
                  <div key={question} className="bg-[#121212] border border-white/10 overflow-hidden">
                    <div className="bg-[#FF3B30]/10 border-b border-white/10 px-4 py-3">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30]">{question}</h3>
                    </div>
                    <div className="p-4 space-y-2">
                      {fighters.map((f, idx) => (
                        <div key={f.fighter} className={`flex items-center justify-between py-2 ${idx === 0 ? 'text-white' : 'text-white/60'}`}>
                          <div className="flex items-center gap-3">
                            <span className="text-xs text-white/40 w-5">{idx + 1}.</span>
                            <span className={`text-sm ${idx === 0 ? 'font-bold' : ''}`}>{f.fighter}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="bg-white/10 h-2" style={{ width: `${Math.max(20, (f.votes / fighters[0].votes) * 80)}px` }} />
                            <span className="text-sm font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{f.votes}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Predictions Tab */}

        {/* Votes Tab (audience voting) */}
        {activeTab === "votes" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>Audience Votes</h2>
              <div className="flex items-center gap-3">
                <Button onClick={handleExportVoteEmails}
                  className="bg-[#007AFF] text-white hover:bg-[#3395FF] font-bold uppercase tracking-wider px-4 py-2 rounded-none text-xs"
                  data-testid="export-vote-emails-btn">
                  <Download className="w-3 h-3 mr-2" /> Export Emails
                </Button>
                <Button onClick={handleDeleteAllVotes}
                  className="bg-transparent border border-red-500/50 text-red-500 hover:bg-red-500/10 font-bold uppercase tracking-wider px-4 py-2 rounded-none text-xs"
                  data-testid="delete-all-votes-btn">
                  <Trash2 className="w-3 h-3 mr-2" /> Clear All Votes
                </Button>
              </div>
            </div>

            {/* Vote Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#121212] border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Total Votes</div>
                <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                  {totalVoteCount}
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Lottery Emails</div>
                <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{voteEmails.length}</div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Categories</div>
                <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{Object.keys(voteTally).length}</div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#A1A1AA] font-bold mb-2">Marketing Opt-in</div>
                <div className="text-3xl" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{voteEmails.filter(e => e.marketing_consent).length}</div>
              </div>
            </div>

            {/* Vote Tallies */}
            {Object.keys(voteTally).length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10"><p className="text-[#A1A1AA]">No votes yet</p></div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {Object.entries(voteTally).map(([category, entries]) => (
                  <div key={category} className="bg-[#121212] border border-white/10 overflow-hidden">
                    <div className="bg-[#FF3B30]/10 border-b border-white/10 px-4 py-3">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30]">{category}</h3>
                      <p className="text-[10px] text-[#A1A1AA]">{entries.reduce((s, e) => s + e.votes, 0)} total votes</p>
                    </div>
                    <div className="p-4 space-y-2">
                      {entries.map((e, idx) => (
                        <div key={e.name} className={`flex items-center justify-between py-1 ${idx === 0 ? 'text-white' : 'text-white/60'}`}>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-white/40 w-5">{idx + 1}.</span>
                            <span className={`text-sm ${idx === 0 ? 'font-bold' : ''}`}>{e.name}</span>
                          </div>
                          <span className="text-sm font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{e.votes}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Vote Emails List */}
            {voteEmails.length > 0 && (
              <div className="bg-[#121212] border border-white/10 overflow-hidden">
                <div className="border-b border-white/10 px-4 py-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider">Lottery Registrations ({voteEmails.length})</h3>
                </div>
                <table className="w-full">
                  <thead><tr className="border-b border-white/10">
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Email</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Marketing</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Date</th>
                  </tr></thead>
                  <tbody>
                    {voteEmails.map(e => (
                      <tr key={e.id} className="border-b border-white/5">
                        <td className="p-4">{e.email}</td>
                        <td className="p-4"><span className={`text-xs px-2 py-1 ${e.marketing_consent ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}>{e.marketing_consent ? 'Yes' : 'No'}</span></td>
                        <td className="p-4 text-sm text-[#A1A1AA]">{new Date(e.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}


        {activeTab === "predictions" && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>All Predictions</h2>
              <div className="flex items-center gap-3">
                {predictions.length > 0 && (
                  <>
                    <Button onClick={handleExportExcel}
                      className="bg-[#007AFF] text-white hover:bg-[#3395FF] font-bold uppercase tracking-wider px-4 py-2 rounded-none text-xs"
                      data-testid="export-excel-button">
                      <Download className="w-3 h-3 mr-2" /> Export Excel
                    </Button>
                    <Button onClick={handleDeleteAllPredictions}
                      className="bg-transparent border border-red-500/50 text-red-500 hover:bg-red-500/10 font-bold uppercase tracking-wider px-4 py-2 rounded-none text-xs"
                      data-testid="delete-all-predictions-btn">
                      <Trash2 className="w-3 h-3 mr-2" /> Clear All
                    </Button>
                  </>
                )}
              </div>
            </div>
            {predictions.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <Users className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" /><p className="text-[#A1A1AA]">No predictions yet</p>
              </div>
            ) : (
              <div className="bg-[#121212] border border-white/10 overflow-hidden">
                <table className="w-full" data-testid="predictions-table">
                  <thead><tr className="border-b border-white/10">
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Email</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Lottery</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Picks</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4">Date</th>
                    <th className="text-left text-xs uppercase tracking-wider text-[#A1A1AA] p-4 w-12"></th>
                  </tr></thead>
                  <tbody>
                    {predictions.map(pred => (
                      <tr key={pred.id} className="border-b border-white/5">
                        <td className="p-4">{pred.email}</td>
                        <td className="p-4">
                          <span className={`text-xs px-2 py-1 ${pred.marketing_consent ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}>
                            {pred.marketing_consent ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {pred.predictions.map((p) => {
                              const fight = fights.find(f => f.id === p.fight_id);
                              const name = fight ? fight[p.winner]?.name : p.winner;
                              return (
                                <span key={p.fight_id} className="text-xs px-2 py-1 bg-white/5 text-white/80">{name || p.winner}</span>
                              );
                            })}
                          </div>
                        </td>
                        <td className="p-4 text-sm text-[#A1A1AA]">{new Date(pred.created_at).toLocaleDateString()}</td>
                        <td className="p-4">
                          <Button onClick={() => handleDeletePrediction(pred.id)} variant="outline" size="sm"
                            className="bg-transparent border border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none h-7 w-7 p-0"
                            data-testid={`delete-prediction-${pred.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* Archive Tab */}
        {activeTab === "archive" && (
          <section>
            <h2 className="text-2xl uppercase tracking-tight mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <Archive className="w-6 h-6 inline mr-2" />
              Tapahtuma-arkisto
            </h2>

            {/* Archive Current Event */}
            <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="archive-form">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#FF3B30] mb-4">Arkistoi nykyinen tapahtuma</h3>
              <p className="text-xs text-[#A1A1AA] mb-4">
                Tämä tallentaa kaikki nykyiset ottelut, veikkaukset ja äänet arkistoon ja tyhjentää aktiivisen näkymän uutta tapahtumaa varten.
              </p>
              <div className="flex gap-3 items-end flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs uppercase tracking-wider text-[#A1A1AA]">Tapahtuman nimi</Label>
                  <Input value={archiveEventName} onChange={(e) => setArchiveEventName(e.target.value)}
                    placeholder="esim. Hamara MMA Vol 9"
                    className="bg-transparent border border-white/20 text-white focus:border-[#FF3B30] rounded-none mt-1"
                    data-testid="archive-event-name-input" />
                </div>
                <Button onClick={handleArchiveEvent} disabled={archiving || fights.length === 0}
                  className="bg-[#FF3B30] text-white hover:bg-[#FF5A52] font-bold uppercase tracking-wider px-6 py-2 rounded-none disabled:opacity-50"
                  data-testid="archive-event-button">
                  <Archive className="w-4 h-4 mr-2" />
                  {archiving ? "Arkistoidaan..." : "Arkistoi"}
                </Button>
              </div>
              {fights.length === 0 && (
                <p className="text-xs text-yellow-500 mt-2">Ei otteluita arkistoitavaksi.</p>
              )}
              {fights.length > 0 && (
                <p className="text-xs text-[#A1A1AA] mt-2">
                  Arkistoidaan: {fights.length} ottelua, {predictions.length} veikkausta
                </p>
              )}
            </div>

            {/* Archived Events List */}
            <h3 className="text-lg uppercase tracking-tight mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              Arkistoidut tapahtumat ({archivedEvents.length})
            </h3>
            {archivedEvents.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10" data-testid="no-archived-events">
                <Archive className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
                <p className="text-[#A1A1AA]">Ei arkistoituja tapahtumia</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="archived-events-list">
                {archivedEvents.map(event => (
                  <div key={event.id} className="bg-[#121212] border border-white/10 p-5 flex items-center justify-between flex-wrap gap-4"
                    data-testid={`archived-event-${event.id}`}>
                    <div>
                      <h4 className="text-xl uppercase tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{event.name}</h4>
                      <p className="text-xs text-[#A1A1AA]">
                        {new Date(event.archived_at).toLocaleDateString("fi-FI")} &middot; {event.fight_count} ottelua &middot; {event.prediction_count} veikkausta
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => handleUnarchiveEvent(event.id, event.name)}
                        className="bg-transparent border border-[#007AFF]/50 text-[#007AFF] hover:bg-[#007AFF]/10 rounded-none text-xs uppercase tracking-wider px-4 py-2"
                        data-testid={`unarchive-event-${event.id}`}>
                        <RotateCcw className="w-3 h-3 mr-2" /> Palauta
                      </Button>
                      <Button onClick={() => handleDeleteArchivedEvent(event.id, event.name)}
                        className="bg-transparent border border-red-500/50 text-red-500 hover:bg-red-500/10 rounded-none text-xs uppercase tracking-wider px-4 py-2"
                        data-testid={`delete-archived-event-${event.id}`}>
                        <Trash2 className="w-3 h-3 mr-2" /> Poista
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
